import './App.css'
import { Stack, ThemeProvider, Toolbar } from '@mui/material'
import { useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { EntityTypesProvider } from './io/context/EntityTypes'
import LandingPage from './pages/LandingPage'
import { createEiduTheme } from './ui/theme/eiduTheme'
import { getTheme, getThemeConfig } from './ui/theme/themeConfig'
import { createAuthContext } from './io/api/authorization/AuthenticationContext'
import TopAppBar from './components/layout/TopAppBar'
import { logAuthContext } from './util/Debug'
import { FormErrorProvider } from './util/FormErrorBus'
import { setGlobalAuthContext } from './io/api/authorization/GlobalAuthContext'
import ScreenSpanTracker from './otel/ScreenSpanTracker'
import { COMPACT_LAYOUT_MAX_VIEWPORT_WIDTH_PX } from './ui/theme/layoutBreakpoints'

const getViewportWidth = () =>
  typeof window === 'undefined' ? COMPACT_LAYOUT_MAX_VIEWPORT_WIDTH_PX + 1 : window.innerWidth

const App = () => {
  logAuthContext()
  const iAuthContext = useContext<IAuthContext>(AuthContext)
  const authContext = createAuthContext(iAuthContext)
  const { tokenData, logOut } = iAuthContext

  const organizationId = iAuthContext?.idTokenData?.organization_id ?? iAuthContext?.idTokenData?.organizationId
  const userEntityId = iAuthContext?.idTokenData?.user_entity_id ?? iAuthContext?.idTokenData?.userEntityId
  const theme = getTheme(organizationId)
  const themeConfig = getThemeConfig(theme)
  const eiduTheme = useMemo(() => createEiduTheme(themeConfig.primaryMain), [themeConfig.primaryMain])
  const [navigationOpen, setNavigationOpen] = useState(false)
  const [viewportWidth, setViewportWidth] = useState(getViewportWidth)
  const compactLayout = viewportWidth <= COMPACT_LAYOUT_MAX_VIEWPORT_WIDTH_PX

  useEffect(() => {
    setGlobalAuthContext(iAuthContext)
  }, [iAuthContext])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const handleResize = () => {
      setViewportWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    if (!compactLayout) {
      setNavigationOpen(false)
    }
  }, [compactLayout])

  const appContent = () => {
    if (!tokenData) return null
    else
      return (
        <ThemeProvider theme={eiduTheme}>
          <Stack className="app">
            <TopAppBar
              authContext={authContext}
              signOut={() => logOut()}
              theme={theme}
              showNavigationMenuButton={compactLayout}
              onNavigationMenuClick={() => setNavigationOpen(true)}
            />
            <Toolbar />
            <Stack sx={{ flex: 1, minHeight: 0 }}>
              <LandingPage
                overlayNavigation={compactLayout}
                navigationOpen={navigationOpen}
                onNavigationClose={() => setNavigationOpen(false)}
              />
            </Stack>
          </Stack>
        </ThemeProvider>
      )
  }

  return (
    <FormErrorProvider>
      <EntityTypesProvider>
        <ScreenSpanTracker organizationId={organizationId} userEntityId={userEntityId} />
        {appContent()}
      </EntityTypesProvider>
    </FormErrorProvider>
  )
}

export default App
